import {
  ADMIN_PORTAL_PARENT_ORIGIN,
  ADMIN_PORTAL_REQUIRE_POSTMESSAGE_PARENT_SOURCE,
  LOGGING_ENABLED,
} from '../config.js';
import { markOnce, PERF_MARKS } from './perf-metrics.js';

/** Incoming: admin portal sends Auth0 access token (used like existing JWT login). */
export const PARENT_AUTH_TOKEN_MESSAGE_TYPE = 'angkas-aap:messenger-auth-token';

/** Incoming: admin portal sends the topic the user should be loaded into after auth. */
export const MESSENGER_TOPIC_RECEIVED_MESSAGE_TYPE = 'angkas-aap:messenger-topic-name';

/** Outgoing: iframe tells parent the listener is registered. */
export const MESSENGER_READY_MESSAGE_TYPE = 'messenger:ready';

/** Outgoing: iframe accepted token; parent may stop retries. */
export const MESSENGER_TOKEN_RECEIVED_MESSAGE_TYPE = 'messenger:token-received';

let parentMessengerToken = null;
let parentMessengerTopic = null;
let initialized = false;

function isAllowedOrigin(origin) {
  if (!ADMIN_PORTAL_PARENT_ORIGIN || !origin || typeof origin !== 'string') {
    return false;
  }
  return origin === ADMIN_PORTAL_PARENT_ORIGIN;
}

/** targetOrigin for child → parent postMessage (e.g. messenger:ready). Never use '*'. */
function resolvePostMessageTargetOrigin() {
  return ADMIN_PORTAL_PARENT_ORIGIN || null;
}

function postToParent(payload, targetOrigin) {
  if (typeof globalThis === 'undefined' || globalThis.parent === globalThis) {
    return;
  }
  if (!targetOrigin || !isAllowedOrigin(targetOrigin)) {
    return;
  }
  globalThis.parent.postMessage(payload, targetOrigin);
}

function logInfo(...args) {
  if (LOGGING_ENABLED) {
    console.info(`[${new Date().toLocaleTimeString()}] [AdminParentAuth]`, ...args);
  }
}

/**
 * Normalize an incoming postMessage payload into a list of message entries.
 * The admin portal now sends an array of typed objects (token + topic), but a
 * single object is still accepted for backward compatibility.
 */
function normalizeMessageEntries(data) {
  if (Array.isArray(data)) {
    return data.filter(entry => entry && typeof entry === 'object');
  }
  if (data && typeof data === 'object') {
    return [data];
  }
  return [];
}

function findEntryByType(entries, type) {
  return entries.find(entry => entry.type === type) || null;
}

function extractToken(entries) {
  const authEntry = findEntryByType(entries, PARENT_AUTH_TOKEN_MESSAGE_TYPE);
  if (!authEntry) {
    return '';
  }
  return typeof authEntry.token === 'string' ? authEntry.token.trim() : '';
}

function extractTopicName(entries) {
  const topicEntry = findEntryByType(entries, MESSENGER_TOPIC_RECEIVED_MESSAGE_TYPE);
  if (!topicEntry) {
    return '';
  }
  return typeof topicEntry.topicName === 'string' ? topicEntry.topicName.trim() : '';
}

function handleMessage(event) {
  if (!ADMIN_PORTAL_PARENT_ORIGIN) {
    return;
  }

  const data = event.data;
  const entries = normalizeMessageEntries(data);
  const looksLikeAuthMessage = !!findEntryByType(entries, PARENT_AUTH_TOKEN_MESSAGE_TYPE);

  if (!isAllowedOrigin(event.origin)) {
    if (LOGGING_ENABLED && looksLikeAuthMessage) {
      logInfo(
        'ignored auth message: origin not in allowlist',
        { origin: event.origin, expectedOrigin: ADMIN_PORTAL_PARENT_ORIGIN }
      );
    }
    return;
  }

  if (globalThis.parent !== globalThis && ADMIN_PORTAL_REQUIRE_POSTMESSAGE_PARENT_SOURCE) {
    if (event.source !== globalThis.parent) {
      if (LOGGING_ENABLED) {
        logInfo('rejected: embedded but event.source !== globalThis.parent', {
          origin: event.origin,
          requireParentSource: ADMIN_PORTAL_REQUIRE_POSTMESSAGE_PARENT_SOURCE,
        });
      }
      return;
    }
  }

  if (entries.length === 0) {
    if (LOGGING_ENABLED) {
      logInfo('ignored: payload is neither an object nor an array of objects', {
        origin: event.origin,
        payloadType: Array.isArray(data) ? 'array' : typeof data,
      });
    }
    return;
  }

  const token = extractToken(entries);
  if (!token) {
    if (LOGGING_ENABLED) {
      logInfo('rejected: missing or empty auth token entry', {
        origin: event.origin,
        entryTypes: entries.map(entry => entry.type),
      });
    }
    return;
  }

  const topicName = extractTopicName(entries);

  // Perf: parent auth token accepted (start of the token → login stage).
  markOnce(PERF_MARKS.TOKEN_RECEIVED);

  parentMessengerToken = token;
  try {
    globalThis.__ANGKAS_JWT__ = token;
  } catch (err) {
    if (LOGGING_ENABLED) {
      logInfo('could not assign globalThis.__ANGKAS_JWT__', {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (topicName) {
    parentMessengerTopic = topicName;
    logInfo('accepted topic', { origin: event.origin, type: MESSENGER_TOPIC_RECEIVED_MESSAGE_TYPE });
  } else if (LOGGING_ENABLED) {
    logInfo('no topic entry in payload; user will land on default view', {
      origin: event.origin,
    });
  }

  logInfo('accepted token', { origin: event.origin, tokenLength: token.length });

  postToParent({ type: MESSENGER_TOKEN_RECEIVED_MESSAGE_TYPE }, event.origin);
  if (LOGGING_ENABLED) {
    logInfo('posted ack to parent', MESSENGER_TOKEN_RECEIVED_MESSAGE_TYPE, { targetOrigin: event.origin });
  }

  try {
    globalThis.dispatchEvent(new Event('angkas-jwt-ready'));
  } catch (err) {
    if (LOGGING_ENABLED) {
      logInfo('dispatchEvent(angkas-jwt-ready) failed', {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }
  if (LOGGING_ENABLED) {
    logInfo('dispatched angkas-jwt-ready');
  }
}

/**
 * Call once at app bootstrap (before async work) so the message listener is registered early.
 */
export function initAdminParentAuth() {
  if (typeof globalThis === 'undefined') {
    return;
  }
  if (initialized) {
    if (LOGGING_ENABLED) {
      logInfo('init skipped (already initialized)');
    }
    return;
  }
  initialized = true;

  if (!ADMIN_PORTAL_PARENT_ORIGIN) {
    if (LOGGING_ENABLED) {
      logInfo('init: iframe auth disabled (REACT_APP_ADMIN_PORTAL_PARENT_ORIGIN unset)');
    }
    return;
  }

  globalThis.addEventListener('message', handleMessage, false);
  if (LOGGING_ENABLED) {
    const embedded = globalThis.parent !== globalThis;
    const referrerOrigin = (() => {
      try {
        return document.referrer ? new URL(document.referrer).origin : '';
      } catch (err) {
        logInfo('init: invalid document.referrer for logging', {
          message: err instanceof Error ? err.message : String(err),
        });
        return '(invalid referrer)';
      }
    })();
    logInfo('init: message listener registered', {
      trustedOrigin: ADMIN_PORTAL_PARENT_ORIGIN,
      embedded,
      referrerOrigin: referrerOrigin || '(empty)',
      requireParentSource: ADMIN_PORTAL_REQUIRE_POSTMESSAGE_PARENT_SOURCE,
    });
  }

  const readyTarget = resolvePostMessageTargetOrigin();
  if (readyTarget && globalThis.parent !== globalThis) {
    postToParent({ type: MESSENGER_READY_MESSAGE_TYPE }, readyTarget);
    // Perf: handshake sent to parent (start of the embedded auth → topic flow).
    markOnce(PERF_MARKS.READY_SENT);
    logInfo('posted handshake to parent', MESSENGER_READY_MESSAGE_TYPE, { targetOrigin: readyTarget });
  } else if (LOGGING_ENABLED) {
    logInfo('handshake skipped', {
      reason: globalThis.parent === globalThis ? 'not embedded (parent === self)' : 'could not resolve targetOrigin',
      hint: 'set REACT_APP_ADMIN_PORTAL_PARENT_ORIGIN or match document.referrer to that origin',
      resolvedReadyTarget: readyTarget || null,
    });
  }
}

/** Token from last valid admin postMessage (for HTTP client / login fallback). Never logged here. */
export function getAdminParentMessengerToken() {
  return parentMessengerToken;
}

/** Topic name from last valid admin postMessage; used to load the user into the right topic after auth. */
export function getAdminParentMessengerTopic() {
  return parentMessengerTopic;
}

export function clearAdminParentMessengerToken() {
  const had = Boolean(parentMessengerToken);
  parentMessengerToken = null;
  parentMessengerTopic = null;
  try {
    if (globalThis.__ANGKAS_JWT__) {
      delete globalThis.__ANGKAS_JWT__;
    }
  } catch (err) {
    if (LOGGING_ENABLED) {
      logInfo('clearAdminParentMessengerToken: could not delete globalThis.__ANGKAS_JWT__', {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }
  if (LOGGING_ENABLED && had) {
    logInfo('cleared stored parent messenger token');
  }
}
